import React, { useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  ChartData,
  ChartOptions,
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  Tooltip,
  BarElement,
  Plugin,
  Legend,
  LegendItem
} from 'chart.js';
import merge from 'lodash.merge';
import styles from './base-chart.module.scss';
import { BaseChartLegend } from './base-chart-legend';
import { colors } from '@utils/colors';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const months = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember'
];

export const monthLabels = months.map(month => month.slice(0, 1));

const chartBorderColor = colors.greyMedium;
const chartTextColor = colors.greyDevil;
const paddingChartTop = 10;
const paddingTickY = 8;

const customScale: Plugin = {
  id: 'customScale',
  beforeDatasetsDraw: chart => {
    const {
      ctx,
      chartArea: { bottom },
      scales: { x }
    } = chart;

    ctx.save();

    x.ticks.forEach(tick => {
      if (tick.label && typeof tick.label === 'string') {
        ctx.fillStyle = chartTextColor;
        ctx.textAlign = 'right';
        ctx.fillText(tick.label, x.getPixelForValue(tick.value), bottom + 25);
      }
      ctx.restore();
    });
  }
};

const defaultChartOptions = (unit: string): ChartOptions<'bar'> => ({
  maintainAspectRatio: false,
  resizeDelay: 250,
  font: {
    family: 'MiloADAC',
    size: 12
  },
  color: chartTextColor,
  borderColor: chartBorderColor,
  layout: {
    padding: {
      top: paddingChartTop
    }
  },
  elements: {
    bar: {
      backgroundColor: colors.highlight
    }
  },
  datasets: {
    bar: {
      barPercentage: 0.5
    }
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      displayColors: false,
      callbacks: {
        title: context => months[context[0].dataIndex]
      }
    }
  },
  scales: {
    y: {
      position: 'right',
      border: {
        display: false
      },
      ticks: {
        padding: paddingTickY,
        crossAlign: 'far',
        callback: value => `${value} ${unit}`
      },
      grid: {
        drawTicks: false
      }
    },
    x: {
      border: {
        display: false
      },
      ticks: {
        display: false
      },
      grid: {
        tickLength: 30,
        // hide last tick (has no index)
        color: tick => (tick.index == null ? colors.white100 : chartBorderColor)
      }
    }
  }
});

type BaseChartProps<ChartDataType> = {
  chartOptions?: ChartOptions<'bar'>;
  chartData: ChartData<'bar', ChartDataType>;
  unit: string;
};

export const BaseChart = <ChartDataType extends unknown>({
  chartOptions,
  chartData,
  unit
}: BaseChartProps<ChartDataType>) => {
  const chartRef = useRef<ChartJS<'bar', ChartDataType>>();
  const [legendItems, setLegendItems] = useState<LegendItem[]>();

  useEffect(() => {
    if (chartRef.current && chartRef.current.legend?.legendItems) {
      setLegendItems(chartRef.current.legend.legendItems);
    }
  }, []);

  return (
    <>
      <div className={styles.chartContainer}>
        <Bar
          ref={chartRef}
          data={chartData}
          options={merge(defaultChartOptions(unit), chartOptions)}
          plugins={[customScale]}
        />
      </div>
      {legendItems && legendItems.length > 0 && <BaseChartLegend items={legendItems} />}
    </>
  );
};
